// PDF order-sheet parser. Extracts product lines from a structured
// quotation/order PDF (tested against Arch-generated quotations) using
// pdf.js for text extraction, then a regex tuned against real extracted
// output — including a pre-clean step for a kerning artifact where a
// stray single letter gets glued onto the start of a price number
// (e.g. "a7.99" instead of "7.99").
//
// This is intentionally permissive about what counts as a "code" (full
// barcodes like 6009656550437 AND short internal codes like 3384 both
// work, since the pattern only requires "a non-whitespace token at the
// start of the line", matching how products.sku is used in this system).

import * as pdfjsLib from 'pdfjs-dist';

// Required so pdf.js can find its worker script — Vite serves this
// from node_modules via the ?url import suffix.
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export type ParsedOrderLine = {
    code: string;
    rawDescription: string;
    unitPrice: number;
    quantity: number;
    discount: number;
    total: number;
    lineNumber: number; // 1-indexed position in the PDF, for dedup/display
};

// Minimal shape of a pdf.js text-content item — only the two fields this
// parser actually reads. Avoids depending on pdfjs-dist's internal type
// paths (TextItem lives under a non-public import path that has moved
// across versions), while still avoiding `any`.
interface PdfTextItem {
    str: string;
    transform: number[];
}

// Confirmed real extraction order from pdf.js for this PDF source
// (Arch-generated quotations): Code, Unit Price, Qty, Disc, Description,
// Total — NOT visual reading order (code, description, price...). This
// differs from how a human reads the table, or how some other PDF text
// extractors reconstruct it; pdf.js emits text items in the underlying
// content-stream order, which for this PDF happens to put price/qty/disc
// before the description, with the running total at the very end.
const LINE_PATTERN = /^(\S+)\s+(\d+\.\d{2})\s+(\d+\.\d{2})\s+(\d+\.\d{2})\s+(.+?)\s+(\d+\.\d{2})$/;

/**
 * Removes a common PDF text-extraction artifact where a single stray
 * letter gets glued directly onto the front of a number, e.g.
 * "a7.99" -> "7.99". Only strips a SINGLE leading letter immediately
 * preceded by whitespace and immediately followed by a digit, so it
 * won't damage real text like "P/KG" or unit codes.
 */
function cleanArtifacts(line: string): string {
    return line.replace(/(?<=\s)[a-zA-Z](\d)/g, '$1');
}

export async function extractTextFromPdf(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const pageTexts: string[] = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        // pdf.js gives individual text items per line fragment; join
        // items with a newline heuristic based on Y position changes
        // would be more robust, but for this consistent table layout,
        // joining by item order with spaces and relying on the regex
        // to find line boundaries via the trailing-numbers pattern
        // works because each row's text items are emitted in order.
        const lines: string[] = [];
        let currentLine = '';
        let lastY: number | null = null;

        for (const item of textContent.items as PdfTextItem[]) {
            const y = item.transform[5];
            if (lastY !== null && Math.abs(y - lastY) > 2) {
                if (currentLine.trim()) lines.push(currentLine.trim());
                currentLine = '';
            }
            currentLine += (currentLine ? ' ' : '') + item.str;
            lastY = y;
        }
        if (currentLine.trim()) lines.push(currentLine.trim());

        pageTexts.push(lines.join('\n'));
    }

    return pageTexts.join('\n');
}

export function parseOrderLines(rawText: string): ParsedOrderLine[] {
    const lines = rawText.split('\n');
    const results: ParsedOrderLine[] = [];

    lines.forEach((rawLine, idx) => {
        const cleaned = cleanArtifacts(rawLine.trim());
        const match = cleaned.match(LINE_PATTERN);
        if (!match) return;

        const [, code, unitPrice, quantity, discount, description, total] = match;

        results.push({
            code,
            rawDescription: description.trim(),
            unitPrice: parseFloat(unitPrice),
            quantity: parseFloat(quantity),
            discount: parseFloat(discount),
            total: parseFloat(total),
            lineNumber: idx + 1,
        });
    });

    return results;
}

export async function parseOrderPdf(file: File): Promise<ParsedOrderLine[]> {
    const text = await extractTextFromPdf(file);

    console.log('[PDF Import] Raw extracted text:\n' + text);
    return parseOrderLines(text);
}